/**
 * hk_os · <AttentionOverlay>
 * ────────────────────────────────────────────────────────────────────
 * Platform-wide emergency overlay. Used for Emergency, VIP, Supervisor
 * Override, Critical Alerts.
 *
 * Behaviour (locked by ADR-HK-2 §22):
 *   - Dark, high-contrast background
 *   - Huge headline (room number or short label)
 *   - Huge status icon
 *   - Strong vibration burst on mount (if `vibrate=true`)
 *   - Immediate voice playback (`voiceKey` auto-plays on open)
 *   - Single CTA only
 *
 * Props:
 *   open                     — boolean
 *   onDismiss                — dismiss handler (also fires when CTA clicked)
 *   variant                  — "emergency" | "vip" | "override" | "critical"
 *   headline                 — big label (usually a room number)
 *   subline                  — supporting line (optional)
 *   voiceKey                 — snippet key to auto-play (default depends on variant)
 *   ctaLabelKey              — voice key for CTA label (default VOICE_START_TASK)
 *   ctaOnClick               — CTA handler
 *   vibrate                  — default true; passes [200,80,200,80,400]
 *   testid
 */
import React, { useEffect } from "react";
import {
  Siren, Crown, ShieldAlert, AlertOctagon,
} from "lucide-react";
import { useVoiceRegistry } from "./useVoiceRegistry";
import { TaskCompletionButton } from "./TaskCompletionButton";

const VARIANT = {
  emergency: {
    icon: Siren,       tone: "bg-red-700",     ring: "ring-red-400",
    accent: "text-red-100", defaultVoice: "VOICE_SUPERVISOR_REQUIRED",
  },
  vip: {
    icon: Crown,       tone: "bg-amber-700",   ring: "ring-amber-400",
    accent: "text-amber-100", defaultVoice: "VOICE_VIP",
  },
  override: {
    icon: ShieldAlert, tone: "bg-slate-800",   ring: "ring-sky-400",
    accent: "text-sky-100", defaultVoice: "VOICE_SUPERVISOR_REQUIRED",
  },
  critical: {
    icon: AlertOctagon, tone: "bg-black",      ring: "ring-red-500",
    accent: "text-red-100", defaultVoice: "VOICE_ERROR",
  },
};

export function AttentionOverlay({
  open,
  onDismiss,
  variant = "emergency",
  headline,
  subline,
  voiceKey,
  ctaLabelKey = "VOICE_START_TASK",
  ctaOnClick,
  vibrate = true,
  testid,
}) {
  const meta = VARIANT[variant] || VARIANT.emergency;
  const key = voiceKey || meta.defaultVoice;
  const { play } = useVoiceRegistry();

  useEffect(() => {
    if (!open) return undefined;
    if (vibrate && typeof navigator !== "undefined" && navigator.vibrate) {
      try { navigator.vibrate([200, 80, 200, 80, 400]); } catch { /* noop */ }
    }
    play(key).catch(() => {});
    return undefined;
  }, [open, vibrate, play, key]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const Icon = meta.icon;
  const handleCta = () => {
    ctaOnClick?.();
    onDismiss?.();
  };

  return (
    <div
      data-testid={testid || `attention-overlay-${variant}`}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={testid ? `${testid}-headline` : "attention-headline"}
      className={[
        "fixed inset-0 z-[100] flex flex-col items-center justify-center",
        "text-white p-6 sm:p-10 gap-6",
        meta.tone,
      ].join(" ")}
    >
      <span className={`rounded-full bg-white/10 p-8 ring-8 ${meta.ring}`} aria-hidden>
        <Icon className="w-24 h-24 sm:w-28 sm:h-28" strokeWidth={2.2} />
      </span>
      {subline && (
        <div className={`text-[18px] sm:text-[22px] font-semibold uppercase tracking-[0.25em] ${meta.accent}`}>
          {subline}
        </div>
      )}
      <div
        id={testid ? `${testid}-headline` : "attention-headline"}
        data-testid={testid ? `${testid}-text` : "attention-overlay-text"}
        className="text-[112px] sm:text-[160px] font-black leading-none tracking-tight text-center select-none"
      >
        {headline || "—"}
      </div>
      <div className="w-full max-w-md">
        <TaskCompletionButton
          voiceKey={ctaLabelKey}
          tone={variant === "emergency" || variant === "critical" ? "danger" : "primary"}
          onClick={handleCta}
          testid={testid ? `${testid}-cta` : "attention-overlay-cta"}
        />
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          data-testid={testid ? `${testid}-dismiss` : "attention-overlay-dismiss"}
          className="text-[13px] font-semibold text-white/70 hover:text-white underline underline-offset-4"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}

export default AttentionOverlay;
